import { useMemo } from 'react';

import { LaunchApplicationType, MedicalInterviewProvider, QuestionnaireVersionFlag } from '@breathelife/types';
import { InsuranceModules, deserializeNodeIdToAnswerPathMap } from '@zinnia/form-engine-sdk';

import { ApplicationProgressProps, getApplicationProgressByModule } from '../../Helpers/assistedApplication/progress';
import { useFetchParticipantsWithMedicalInterviewQuery } from '../../ReactQuery/Participants/participants.queries';
import { useCarrierContext } from '../useCarrierContext';

export function useApplicationProgress(completeApplicationProgressProps: ApplicationProgressProps): number {
  const { features } = useCarrierContext();
  const { application, needsAnalysisOnly, questionnaireResponse } = completeApplicationProgressProps;

  const isAuraNextEnabled =
    features.medicalInterview.enabled &&
    features.medicalInterview.provider === MedicalInterviewProvider.AuraNext &&
    questionnaireResponse &&
    questionnaireResponse.flags.includes(QuestionnaireVersionFlag.AuraNextMedicalInterview);

  const { data: participants } = useFetchParticipantsWithMedicalInterviewQuery(application?.id, {
    enabled: application && isAuraNextEnabled,
  });

  return useMemo(() => {
    if (!application || !questionnaireResponse) return 0;

    const questionnaire = questionnaireResponse.questionnaire;
    const blueprint = questionnaireResponse?.blueprint;
    const serializedNodeIdToAnswerPathMap = questionnaireResponse?.nodeIdToAnswerPath;
    const nodeIdToAnswerPathMap =
      serializedNodeIdToAnswerPathMap && deserializeNodeIdToAnswerPathMap(serializedNodeIdToAnswerPathMap);
    if (
      features.launchApplication.enabled &&
      [LaunchApplicationType.assistedApplication, LaunchApplicationType.coBrowsing].includes(
        features.launchApplication.type,
      ) &&
      !needsAnalysisOnly
    ) {
      return (
        getApplicationProgressByModule({
          application,
          insuranceModule: InsuranceModules.insuranceApplication,
          nodeIdToAnswerPathMap,
          questionnaire,
          blueprint,
          features,
          participants,
          isAuraNextEnabled,
        }) ?? 0
      );
    }
    return (
      getApplicationProgressByModule({
        application,
        insuranceModule: InsuranceModules.needsAnalysis,
        nodeIdToAnswerPathMap,
        questionnaire,
        blueprint,
        features,
        participants,
        isAuraNextEnabled,
      }) ?? 0
    );
  }, [application, features, needsAnalysisOnly, questionnaireResponse]);
}
